import { ApiProjectSettingKey } from "@incendium/api";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { projectService } from "Apis";
import useProjectSettings from "features/project/hooks/useProjectSettings";
import { useNotification, useSelectedProject } from "Hooks";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";

function OnboardingMetaPixelField() {
  const { selectedProject } = useSelectedProject();
  const { settings, setSettings, settingByType } = useProjectSettings();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const savedValue =
    (settingByType(ApiProjectSettingKey.FB_PIXEL)?.value as string) || "";
  const [pixelId, setPixelId] = useState(savedValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPixelId(savedValue);
  }, [savedValue]);

  const dirty = pixelId.trim() !== savedValue.trim();

  const onSave = useCallback(async () => {
    if (!selectedProject || !dirty) {
      return;
    }
    setSaving(true);
    try {
      const payload = {
        key: ApiProjectSettingKey.FB_PIXEL,
        value: pixelId.trim(),
      };
      const existing = settingByType(ApiProjectSettingKey.FB_PIXEL);
      const clientId = selectedProject.clientId as number;
      const projectId = selectedProject.id as number;

      const res = existing.id
        ? await projectService.projectsServiceUpdateProjectSetting({
            clientId,
            projectId,
            settingId: existing.id,
            payload,
          })
        : await projectService.projectsServiceCreateProjectSetting({
            clientId,
            projectId,
            payload,
          });

      setSettings(
        produce(settings, (draft) => {
          const idx = draft.findIndex(
            (s) => s.key === ApiProjectSettingKey.FB_PIXEL,
          );
          if (idx >= 0) {
            draft[idx] = res;
          } else {
            draft.push(res);
          }
        }),
      );
      showSuccessNotification("Meta Pixel ID saved");
    } catch {
      showErrorNotification("Failed to save Meta Pixel ID. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [
    selectedProject,
    dirty,
    pixelId,
    settings,
    setSettings,
    settingByType,
    showSuccessNotification,
    showErrorNotification,
  ]);

  return (
    <Stack
      spacing={1.5}
      sx={{
        mb: 2,
        pb: 2,
        borderBottom: 1,
        borderColor: "grey.200",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Meta Pixel (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add your pixel ID if you want Incendium to send conversion events back
        to Meta. You will still ned to select which events to fire from signals
        once onboard. You can skip this for now and add it later.
      </Typography>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        label="Pixel ID"
        placeholder="e.g. 123456789012345"
        helperText="Find this in Meta Events Manager"
        value={pixelId}
        onChange={(e) => setPixelId(e.target.value)}
      />
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={onSave}
        disabled={!dirty || saving}
        sx={{ alignSelf: "flex-end" }}
      >
        {saving ? "Saving…" : "Save pixel ID"}
      </Button>
    </Stack>
  );
}

export default OnboardingMetaPixelField;
